import type { UpdateMeMutation, UpdateMeMutationVariables } from '@/api/generated/graphql';
import { UPDATE_ME_MUTATION } from '@/api/mutations/me';
import { useAuthedClient } from '@/api/useAuthedClient';

import { useGraphQLMutation } from './useGraphQLMutation';
import type { Me } from './useMe';

// Client-intent narrowing of the generated UpdateMeInput: the profile form
// only ever sends strings (omit to leave a field unchanged), never null.
// The request below is typed with the generated variables, so any drift
// from the schema input still fails typecheck.
export interface UpdateMeInput {
  name?: string;
  surname?: string;
  profilePictureLocation?: string;
}

interface UseUpdateMeReturn {
  updateMe: (input: UpdateMeInput) => Promise<Me | null>;
  isSubmitting: boolean;
  error: Error | null;
}

export function useUpdateMe(): UseUpdateMeReturn {
  const authedClient = useAuthedClient();
  const { mutate, isSubmitting, error } = useGraphQLMutation<UpdateMeInput, Me>({
    mutationFn: async (input, signal) => {
      const response = await authedClient.request<UpdateMeMutation, UpdateMeMutationVariables>({
        document: UPDATE_ME_MUTATION,
        variables: { input },
        signal,
      });
      return response.updateMe;
    },
  });

  return { updateMe: mutate, isSubmitting, error };
}
